import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LockIcon from '@mui/icons-material/Lock';
import GlowCard from '../../../shared/GlowCard';
import { useColors } from '../../../theme/ColorModeContext';
import { submitTheoryAnswer } from '../../../api/Api';
import { TheoryQuestionData } from './types';

type ChoiceKey = 'a' | 'b' | 'c' | 'd';
const CHOICE_KEYS: ChoiceKey[] = ['a', 'b', 'c', 'd'];

interface QuizQuestionProps {
    token: string;
    category: string;
    stageSlug: string;
    question: TheoryQuestionData;
    solvedAlready: boolean;
    onCorrect: (stageCompleted: boolean) => void;
}

const QuizQuestion = ({ token, category, stageSlug, question, solvedAlready, onCorrect }: QuizQuestionProps) => {
    const colors = useColors();
    const [submitting, setSubmitting] = useState(false);
    const [wrongChoice, setWrongChoice] = useState<ChoiceKey | null>(null);
    const [correctChoice, setCorrectChoice] = useState<ChoiceKey | null>(question.correct_choice || null);
    const [explanation, setExplanation] = useState<string | null>(question.explanation || null);
    const [solved, setSolved] = useState(solvedAlready);

    const handleSelect = async (choice: ChoiceKey) => {
        if (submitting || solved || !token) return;
        setSubmitting(true);
        setWrongChoice(null);

        const result = await submitTheoryAnswer(token, category, stageSlug, question.id, choice);

        setSubmitting(false);

        if (!result) return;

        if (result.correct) {
            setCorrectChoice(choice);
            setExplanation(result.explanation || null);
            setSolved(true);
            onCorrect(!!result.stage_completed);
        } else {
            setWrongChoice(choice);
        }
    };

    return (
        <GlowCard glowOnHover={false} gradientBorder sx={{ mb: '24px' }}>
            <Box sx={{ p: { xs: '20px', md: '28px' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: 2 }}>
                    <HelpOutlineIcon sx={{ color: colors.accent.cyan }} />
                    <Typography sx={{
                        color: colors.accent.cyan,
                        fontFamily: '"Orbitron", sans-serif',
                        fontWeight: 700,
                        fontSize: '0.78rem',
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                    }}>
                        Quick check
                    </Typography>
                </Box>

                <Typography sx={{
                    color: colors.text.primary,
                    fontFamily: '"Orbitron", sans-serif',
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    lineHeight: 1.5,
                    mb: 2.5,
                }}>
                    {question.question}
                </Typography>

                <Box sx={{ display: 'grid', gap: '10px' }}>
                    {CHOICE_KEYS.map((key) => {
                        const isCorrect = key === correctChoice;
                        const isWrong = key === wrongChoice;
                        const isDisabled = submitting || solved || !token;

                        return (
                            <Box
                                key={key}
                                onClick={() => !isDisabled && handleSelect(key)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    p: '14px 16px',
                                    borderRadius: '8px',
                                    cursor: isDisabled ? 'default' : 'pointer',
                                    border: `1px solid ${isCorrect ? colors.accent.green : isWrong ? colors.accent.red : colors.border.subtle}`,
                                    backgroundColor: isCorrect
                                        ? `${colors.accent.green}1a`
                                        : isWrong
                                            ? `${colors.accent.red}1a`
                                            : colors.bg.tertiary,
                                    opacity: solved && !isCorrect ? 0.55 : 1,
                                    transition: 'border-color 0.2s ease, background-color 0.2s ease',
                                    '&:hover': isDisabled ? {} : {
                                        borderColor: colors.accent.cyan,
                                    },
                                }}
                            >
                                <Box sx={{
                                    width: 26,
                                    height: 26,
                                    flexShrink: 0,
                                    borderRadius: '6px',
                                    display: 'grid',
                                    placeItems: 'center',
                                    fontFamily: '"Share Tech Mono", monospace',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                    color: colors.bg.primary,
                                    backgroundColor: isCorrect ? colors.accent.green : isWrong ? colors.accent.red : colors.text.muted,
                                }}>
                                    {key}
                                </Box>
                                <Typography sx={{ color: colors.text.secondary, flexGrow: 1 }}>
                                    {question.choices[key]}
                                </Typography>
                                {isCorrect && <CheckCircleIcon sx={{ color: colors.accent.green }} />}
                                {isWrong && <CancelIcon sx={{ color: colors.accent.red }} />}
                            </Box>
                        );
                    })}
                </Box>

                {!token && !solved && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '12px',
                        mt: 2,
                        p: '12px 16px',
                        borderRadius: '8px',
                        border: `1px solid ${colors.border.subtle}`,
                        backgroundColor: colors.bg.tertiary,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <LockIcon sx={{ color: colors.text.muted, fontSize: '1.1rem' }} />
                            <Typography sx={{ color: colors.text.secondary, fontSize: '0.88rem' }}>
                                Sign up to answer and keep going.
                            </Typography>
                        </Box>
                        <Button
                            component={RouterLink}
                            to="/register"
                            variant="outlined"
                            size="small"
                            sx={{ borderColor: colors.accent.cyan, color: colors.accent.cyan }}
                        >
                            Sign up
                        </Button>
                    </Box>
                )}

                {token && wrongChoice && !solved && (
                    <Typography sx={{ color: colors.accent.red, fontSize: '0.9rem', mt: 2 }}>
                        Not quite. Give it another try.
                    </Typography>
                )}

                {solved && explanation && (
                    <Typography sx={{ color: colors.text.secondary, fontSize: '0.9rem', mt: 2, lineHeight: 1.6 }}>
                        {explanation}
                    </Typography>
                )}
            </Box>
        </GlowCard>
    );
};

export default QuizQuestion;
